import { useEffect, useMemo, useState } from 'react';
import {
  kindFromName,
  normalizeBookingMedia,
  resolveMediaObject,
} from '../lib/bookingMedia';

export function mediaCount(row) {
  return normalizeBookingMedia(row?.media).length;
}

export default function BookingMedia({ media, title = 'From the customer' }) {
  const source = useMemo(() => normalizeBookingMedia(media), [media]);
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    const revokeUrls = [];

    async function load() {
      if (source.length === 0) {
        if (alive) {
          setItems([]);
          setError('');
          setLoading(false);
        }
        return;
      }
      setLoading(true);
      const next = await Promise.all(
        source.map(async (item, index) => {
          const kind = item.kind || kindFromName(item.name || item.path || '');
          const name = item.name || item.path?.split('/').pop() || 'Attachment';
          if (!item.path && item.url) {
            return {
              key: `${index}`,
              kind,
              name,
              url: item.url,
              failed: false,
            };
          }
          const resolved = await resolveMediaObject(item);
          if (resolved.revoke && resolved.url) revokeUrls.push(resolved.url);
          return {
            key: `${item.bucket || 'vehicle-photos'}:${item.path || index}`,
            kind,
            name,
            url: resolved.url,
            failed: !resolved.url,
            error: resolved.error,
          };
        })
      );
      if (!alive) {
        revokeUrls.forEach((url) => URL.revokeObjectURL(url));
        return;
      }
      setItems(next);
      setError(
        next.some((item) => item.failed)
          ? 'Some files could not be opened. Check storage access for staff accounts.'
          : ''
      );
      setLoading(false);
    }

    load();
    return () => {
      alive = false;
      revokeUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [source]);

  if (source.length === 0) return null;

  return (
    <article className="job-card booking-media">
      <h2>{title}</h2>
      <p className="booking-media-lede">
        {source.length === 1
          ? '1 file attached with this booking.'
          : `${source.length} files attached with this booking.`}
      </p>
      {loading ? <p className="booking-media-lede">Loading attachments…</p> : null}
      {error ? <p className="form-error">{error}</p> : null}
      <div className="media-grid">
        {items.map((item) => (
          <MediaTile key={item.key} item={item} />
        ))}
      </div>
    </article>
  );
}

function MediaTile({ item }) {
  const label = item.name || 'Attachment';
  if (!item.url) {
    return (
      <div className="media-tile is-missing">
        <span className="material-symbol" aria-hidden="true">
          {item.kind === 'video'
            ? 'videocam_off'
            : item.kind === 'audio'
              ? 'mic_off'
              : 'hide_image'}
        </span>
        <strong>{label}</strong>
        <small>{item.error || 'Unavailable'}</small>
      </div>
    );
  }
  if (item.kind === 'video') {
    return (
      <figure className="media-tile is-video">
        <video src={item.url} controls preload="metadata" />
        <figcaption>
          <a href={item.url} target="_blank" rel="noreferrer">
            {label}
          </a>
        </figcaption>
      </figure>
    );
  }
  if (item.kind === 'audio') {
    return (
      <figure className="media-tile is-audio">
        <audio src={item.url} controls preload="metadata" />
        <figcaption>{label}</figcaption>
      </figure>
    );
  }
  if (item.kind === 'photo' || kindFromName(label) === 'photo') {
    return (
      <a
        className="media-tile is-photo"
        href={item.url}
        target="_blank"
        rel="noreferrer"
      >
        <img src={item.url} alt={label} />
        <span>{label}</span>
      </a>
    );
  }
  return (
    <a
      className="media-tile is-file"
      href={item.url}
      target="_blank"
      rel="noreferrer"
    >
      <span className="material-symbol" aria-hidden="true">
        attach_file
      </span>
      <strong>{label}</strong>
      <small>Open file</small>
    </a>
  );
}
